import React from 'react';

const Footer = ({ onNavigate, siteData }) => {
    const handleNavigation = (section) => {
        if (onNavigate) {
            onNavigate(section);
        }
    };

    return (
        <footer className="bg-stone-800 text-white py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    {/* Practice Info */}
                    <div className="lg:col-span-2">
                        <h3 className="text-2xl font-semibold mb-4">
                            {siteData?.navbar?.name || "Dr. Wellness"}
                        </h3>
                        <p className="text-stone-300 mb-6 leading-relaxed max-w-md">
                            Providing compassionate, professional mental health services to help you navigate life's challenges
                            and discover your path to wellness and growth.
                        </p>
                        <div className="flex space-x-4">
                            <a
                                href="https://www.instagram.com/bencobostherapy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sage-100 hover:text-white transition-colors inline-block"
                                aria-label="Instagram"
                            >
                                <i className="bi bi-instagram text-2xl"></i>
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li>
                                <button
                                    onClick={() => handleNavigation('about')}
                                    className="text-stone-300 hover:text-white transition-colors text-left"
                                >
                                    About My Practice
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('services')}
                                    className="text-stone-300 hover:text-white transition-colors text-left"
                                >
                                    Therapy Services
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('resources')}
                                    className="text-stone-300 hover:text-white transition-colors text-left"
                                >
                                    Resources
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('blog')}
                                    className="text-stone-300 hover:text-white transition-colors text-left"
                                >
                                    Blog
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('contact')}
                                    className="text-stone-300 hover:text-white transition-colors text-left"
                                >
                                    Contact
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4">Contact</h4>
                        <div className="space-y-3 text-stone-300">
                            <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <a href={`tel:${siteData?.contact?.phone || "(555) 123-4567"}`} className="hover:text-white transition-colors">
                                    {siteData?.contact?.phone || "(555) 123-4567"}
                                </a>
                            </div>
                            <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <a href={`mailto:${siteData?.contact?.email || "Therapy@bencobos.com"}`} className="hover:text-white transition-colors">
                                    {siteData?.contact?.email || "Therapy@bencobos.com"}
                                </a>
                            </div>
                            <div className="flex items-start space-x-2">
                                <svg className="w-4 h-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{siteData?.contact?.address || "123 Wellness Street, City, State 12345"}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Crisis Resources */}
                <div className="bg-stone-700 rounded-xl p-6 mb-8">
                    <div className="flex items-center mb-4">
                        <svg className="w-6 h-6 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <h4 className="text-lg font-semibold">Crisis Support</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <strong>National Suicide Prevention Lifeline:</strong> <a href="tel:988" className="text-amber-400 hover:text-amber-300 transition-colors">988</a>
                        </div>
                        <div>
                            <strong>Crisis Text Line:</strong> <span className="text-amber-400">Text HOME to 741741</span>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-stone-700 pt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="text-stone-300 text-sm mb-4 md:mb-0">
                            © 2024 {siteData?.navbar?.name || "Dr. Wellness"} Therapy. All rights reserved.
                        </div>
                        <div className="flex space-x-6 text-sm">
                            <a href="#" className="text-stone-300 hover:text-white transition-colors">
                                Privacy Policy
                            </a>
                            <a href="#" className="text-stone-300 hover:text-white transition-colors">
                                Terms of Service
                            </a>
                            <a href="#" className="text-stone-300 hover:text-white transition-colors">
                                HIPAA Notice
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;